export interface QosRun {
  id: string;
  label: string;
  value: number; // measured PUT throughput, obj/s
  cap?: number; // configured QoS ceiling, if this run had one
}

/**
 * The QoS money-shot: one small horizontal bar per WARP run (baseline / capped), filling in as runs
 * complete, with a dashed tick marking each run's configured ceiling. Single measure → single hue
 * (--chart-qos, validated for both themes); values are direct-labelled so identity never rides on
 * colour; text wears text tokens, not the series colour.
 */
export function QosChart({ runs, currentId }: { runs: QosRun[]; currentId?: string }) {
  if (runs.length === 0) return null;
  const X0 = 118; // label column | bar origin
  const X1 = 360; // bar max extent (value labels sit to the right)
  const ROW = 27;
  const BAR = 16;
  const TOP = 20;
  const max = Math.max(...runs.map((r) => Math.max(r.value, r.cap ?? 0))) * 1.08;
  const x = (v: number) => X0 + (v / max) * (X1 - X0);
  const h = TOP + runs.length * ROW + 6;

  // Baseline-anchored bar: square at the axis, 4px rounded data-end.
  const bar = (y: number, w: number) => {
    const r = Math.min(4, w);
    return `M ${X0},${y} H ${X0 + w - r} Q ${X0 + w},${y} ${X0 + w},${y + r} V ${y + BAR - r} Q ${X0 + w},${y + BAR} ${X0 + w - r},${y + BAR} H ${X0} Z`;
  };

  return (
    <figure className="qc" role="img" aria-label={`PUT throughput by run: ${runs.map((r) => `${r.label} ${r.value.toFixed(1)} objects per second`).join(', ')}`}>
      <svg viewBox={`0 0 430 ${h}`}>
        <text className="qc-title" x={X0} y={11}>
          PUT throughput · obj/s
        </text>
        {runs.map((r, i) => {
          const y = TOP + i * ROW + (ROW - BAR) / 2;
          const current = r.id === currentId;
          return (
            <g key={r.id}>
              <title>{`${r.label}: ${r.value.toFixed(1)} obj/s${r.cap ? ` (cap ${r.cap})` : ''}`}</title>
              <text className={`qc-label ${current ? 'on' : ''}`} x={X0 - 8} y={y + BAR - 4}>
                {r.label}
              </text>
              <path className="qc-bar" d={bar(y, Math.max(x(r.value) - X0, 1))} />
              {r.cap != null && (
                <line className="qc-cap" x1={x(r.cap)} x2={x(r.cap)} y1={y - 3} y2={y + BAR + 3} />
              )}
              <text className="qc-value" x={x(r.value) + 6} y={y + BAR - 4}>
                {r.value.toFixed(1)}
              </text>
            </g>
          );
        })}
        <line className="qc-axis" x1={X0} x2={X0} y1={TOP - 4} y2={h - 4} />
      </svg>
      <figcaption className="qc-note">
        dashed tick = configured ceiling — throughput settles to whatever QoS allows
      </figcaption>
    </figure>
  );
}
